import * as XLSX from 'xlsx';

const FORBIDDEN_SHEET_CHARS = /[\\/?*[\]:]/g;

function sanitizeSheetName(name, taken) {
  let base = (name || '카테고리').replace(FORBIDDEN_SHEET_CHARS, '_').slice(0, 28).trim() || '카테고리';
  let candidate = base;
  let suffix = 2;
  while (taken.has(candidate)) {
    candidate = `${base.slice(0, 28 - String(suffix).length - 1)}_${suffix}`;
    suffix += 1;
  }
  taken.add(candidate);
  return candidate;
}

function userNameOf(users, id) {
  if (id == null) return '공동';
  return users.find((u) => u.id === id)?.name || `user${id}`;
}

function buildSummarySheet({ year, month, monthly, weekly, users }) {
  const u1 = userNameOf(users, 1);
  const u2 = userNameOf(users, 2);
  const rows = [];

  rows.push([`${year}년 ${month}월 가계부 요약`]);
  rows.push([]);

  rows.push(['전체 합계']);
  rows.push(['구분', u1, u2, '합산']);
  rows.push(['금액', monthly.user1_amount || 0, monthly.user2_amount || 0, monthly.total_amount || 0]);
  rows.push([]);

  if (monthly.by_category?.length) {
    rows.push(['카테고리별 합계']);
    rows.push(['카테고리', u1, u2, '합산']);
    monthly.by_category.forEach((c) => {
      rows.push([c.category_name, c.user1_amount || 0, c.user2_amount || 0, c.total_amount || 0]);
    });
    rows.push([]);
  }

  if (monthly.by_method?.length) {
    rows.push(['결제수단별 합계']);
    rows.push(['결제수단', '금액']);
    monthly.by_method.forEach((m) => {
      rows.push([m.method_name, m.amount || 0]);
    });
    rows.push([]);
  }

  if (monthly.by_day?.length) {
    rows.push(['일별 합계']);
    rows.push(['날짜', '금액']);
    monthly.by_day.forEach((d) => {
      rows.push([d.date, d.amount || 0]);
    });
    rows.push([]);
  }

  if (weekly?.weeks?.length) {
    rows.push(['주별 합계']);
    rows.push(['주차', '기간', u1, u2, '합산']);
    weekly.weeks.forEach((w) => {
      rows.push([
        `${w.week}주`,
        `${w.start_date} ~ ${w.end_date}`,
        w.user1_amount || 0,
        w.user2_amount || 0,
        w.total_amount || 0,
      ]);
    });
  }

  const ws = XLSX.utils.aoa_to_sheet(rows);
  ws['!cols'] = [{ wch: 18 }, { wch: 24 }, { wch: 14 }, { wch: 14 }, { wch: 14 }];
  return ws;
}

function buildCategorySheet(categoryName, expenses) {
  const total = expenses.reduce((s, e) => s + (e.amount || 0), 0);

  const methodTotals = new Map();
  expenses.forEach((e) => {
    const key = e.method_name || '미지정';
    methodTotals.set(key, (methodTotals.get(key) || 0) + (e.amount || 0));
  });
  const methodRows = Array.from(methodTotals.entries())
    .sort(([, a], [, b]) => b - a);

  const rows = [];
  rows.push([`${categoryName} 요약`]);
  rows.push(['총 합계', total]);
  rows.push([]);

  rows.push(['결제수단별']);
  rows.push(['결제수단', '금액']);
  methodRows.forEach(([name, amount]) => rows.push([name, amount]));
  rows.push([]);

  rows.push(['지출 내역']);
  rows.push(['날짜', '제목', '금액', '결제수단', '사용자', '메모']);
  const sorted = [...expenses].sort((a, b) => (a.used_at < b.used_at ? -1 : 1));
  sorted.forEach((e) => {
    rows.push([
      e.used_at,
      e.title || '',
      e.amount || 0,
      e.method_name || '',
      e.user_name || '',
      e.memo || '',
    ]);
  });

  const ws = XLSX.utils.aoa_to_sheet(rows);
  ws['!cols'] = [{ wch: 12 }, { wch: 24 }, { wch: 12 }, { wch: 14 }, { wch: 10 }, { wch: 30 }];
  return ws;
}

function groupByCategory(expenses) {
  const map = new Map();
  expenses.forEach((e) => {
    const key = e.category_name || '미분류';
    if (!map.has(key)) map.set(key, []);
    map.get(key).push(e);
  });
  return Array.from(map.entries())
    .sort(([a], [b]) => a.localeCompare(b, 'ko'))
    .map(([name, items]) => ({ name, items }));
}

export function downloadCashFlowExcel({ year, month, monthly, weekly, expenses, users }) {
  const wb = XLSX.utils.book_new();
  const taken = new Set();

  const summaryName = sanitizeSheetName('요약', taken);
  XLSX.utils.book_append_sheet(wb, buildSummarySheet({ year, month, monthly, weekly, users }), summaryName);

  groupByCategory(expenses).forEach(({ name, items }) => {
    const sheetName = sanitizeSheetName(name, taken);
    XLSX.utils.book_append_sheet(wb, buildCategorySheet(name, items), sheetName);
  });

  const filename = `cashflow-${year}-${String(month).padStart(2, '0')}.xlsx`;
  XLSX.writeFile(wb, filename);
}
